import { createReducer, Action, on } from '@ngrx/store';

import { DealFilterActions } from './deal-filter.actions';
import { DealFilterState, DealFilterModePage } from './deal-filter.state';
import { DealFilter, DealFilterMode } from '../../features/deal-filter/models/deal-filter.model';
import { measureReducer } from '../../features/shared/utils/runtime-reducer-stats';
import { AppActions } from '../app/app.actions';

export const DEFAULT_MODE = {
  [DealFilterModePage.DatapointOrganizer]: DealFilterMode.All,
  [DealFilterModePage.MarketCompare]: DealFilterMode.All,
  [DealFilterModePage.DealCompare]: DealFilterMode.BasketOnly,
  [DealFilterModePage.LlmRule]: DealFilterMode.DealFilterOnly,
};

export const initialState: DealFilterState = {
  loading: false,
  modeLookup: DEFAULT_MODE,
  sort: { active: 'transactionDisplayName', direction: 'asc' },
};

const featureReducer = measureReducer(
  createReducer(
    initialState,
    on(
      AppActions.LoadInitData,
      (state): DealFilterState => ({ ...state, loading: true, error: undefined })
    ),
    on(
      DealFilterActions.LoadList,
      (state): DealFilterState => ({ ...state, loading: true, error: undefined })
    ),
    on(AppActions.LoadInitDataSuccess, (state, action): DealFilterState => {
      const dealFilterId =
        action.data.assetSettings?.selectedDealFilterId || state.selectedFilterId;
      const filter = action.data.dealFilters?.find((i) => i.id === dealFilterId);
      return {
        ...state,
        loading: false,
        list: action.data.dealFilters,
        error: undefined,
        appliedFilter: filter,
        sort: filter?.sort || state.sort,
        selectedFilterId: filter?.id,
      };
    }),
    on(DealFilterActions.LoadListSuccess, (state, action): DealFilterState => {
      const dealFilterSet = new Map<string, DealFilter>(
        state.list.map((dealFilter) => [dealFilter.id, dealFilter])
      );
      return {
        ...state,
        loading: false,
        list: action.list.map((dealFilter) => {
          const storedDealFilter = dealFilterSet.get(dealFilter.id);

          if (storedDealFilter?.transactionCount && storedDealFilter.transactionIds) {
            return {
              ...dealFilter,
              transactionCount: storedDealFilter.transactionCount,
              transactionIds: storedDealFilter.transactionIds,
            };
          }

          return dealFilter;
        }),
      };
    }),
    on(
      DealFilterActions.SetSort,
      (state, action): DealFilterState => ({ ...state, sort: action.sort })
    ),
    on(DealFilterActions.SetMode, (state, action): DealFilterState => {
      const currentState = state.modeLookup[action.key];

      if (action.condition && currentState !== action.condition) {
        return state;
      }

      return {
        ...state,
        modeLookup: {
          ...state.modeLookup,
          [action.key]: action.mode,
        },
      };
    }),
    on(
      DealFilterActions.CreateOrUpdateDealFilter,
      DealFilterActions.ShareDealFilter,
      (state): DealFilterState => ({ ...state, loading: true })
    ),
    on(
      DealFilterActions.CreateOrUpdateDealFilterSuccess,
      (state, action): DealFilterState => ({ ...state, loading: false, list: action.dealFilters })
    ),
    on(
      DealFilterActions.ShareDealFilterSuccess,
      (state, action): DealFilterState => ({
        ...state,
        loading: false,
        list: state.list.map((filter) =>
          filter.id === action.dealFilter.id ? action.dealFilter : filter
        ),
      })
    ),
    on(
      DealFilterActions.LoadListFail,
      DealFilterActions.DeleteFilterFail,
      DealFilterActions.ShareDealFilterFail,
      DealFilterActions.ApplyFilterFail,
      DealFilterActions.UseFilterFail,
      (state, action): DealFilterState => ({ ...state, loading: false, error: action.error })
    ),
    on(
      DealFilterActions.ApplyFilter,
      (state, action): DealFilterState => ({
        ...state,
        loading: true,
      })
    ),
    on(DealFilterActions.ApplyFilterSuccess, (state, action): DealFilterState => {
      return {
        ...state,
        appliedFilter: action.dealFilter,
        loading: false,
        list: state.list.map((i) => (i.id === action.dealFilter.id ? action.dealFilter : i)),
        selectedFilterId: action.resetSelectedFilter ? null : state.selectedFilterId,
      };
    }),
    on(DealFilterActions.UseFilter, (state, action): DealFilterState => {
      const filter = state.list.find((i) => i.id === action.id);

      return {
        ...state,
        appliedFilter: filter,
        sort: filter?.sort || state.sort,
        selectedFilterId: filter?.id,
        loading: true,
      };
    }),
    on(DealFilterActions.UseFilterSuccess, (state, action): DealFilterState => {
      return {
        ...state,
        appliedFilter: action.dealFilter,
        loading: false,
        list: state?.list?.map((i) => (i.id === action.dealFilter.id ? action.dealFilter : i)),
      };
    }),
    on(DealFilterActions.DeleteFilterSuccess, (state, action): DealFilterState => {
      return {
        ...state,
        list: action.dealFilters,
        appliedFilter: null,
        selectedFilterId: null,
      };
    })
  )
);

export function reducer(state: DealFilterState | undefined, action: Action) {
  return featureReducer(state, action);
}
